import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../components/Input';
import TextArea from '../components/TextArea';
import Button from '../components/Button';
import FormGroup from '../components/FormGroup';
import { useFormData } from '../context/FormDataContext';
import { stripeProducts } from '../stripe-config';

const QueenRegistration = () => {
  const navigate = useNavigate();
  const { setFormData } = useFormData();
  
  const [formState, setFormState] = useState({
    fullName: '',
    email: '',
    phone: '',
    age: '',
    biography: '',
    experience: '',
    photoUrl: '',
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formState.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formState.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formState.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formState.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone number should be 10 digits';
    }
    
    if (!formState.age.trim()) {
      newErrors.age = 'Age is required';
    } else {
      const age = parseInt(formState.age, 10);
      if (isNaN(age) || age < 16 || age > 25) {
        newErrors.age = 'Age must be between 16 and 25';
      }
    }
    
    if (!formState.biography.trim()) newErrors.biography = 'Biography is required';
    if (!formState.experience.trim()) newErrors.experience = 'Experience information is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Generate a mock confirmation number
      const confirmationNumber = Math.floor(1000000 + Math.random() * 9000000).toString();
      
      // Store in context for confirmation page
      setFormData({
        type: 'queen',
        ...formState,
        id: 'mock-id-' + Math.random().toString(36).substring(2, 11),
        confirmationNumber
      });
      
      // Navigate to confirmation page
      navigate('/confirmation/queen');
    } catch (error) {
      console.error('Error submitting form:', error);
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="text-3xl md:text-4xl font-heading font-bold text-primary-900 mb-6">
        Rodeo Queen Application
      </h1>
      
      <p className="text-gray-700 mb-8">
        Apply to become our next Rodeo Queen! Complete the form below to submit your application.
        All fields marked with an asterisk (*) are required.
      </p>
      
      <div className="bg-accent-50 border border-accent-200 p-4 rounded-md mb-6">
        <h3 className="font-semibold text-accent-800 mb-2">Application Fee: ${stripeProducts.rodeoQueen.price.toFixed(2)}</h3>
        <p className="text-sm text-gray-700">{stripeProducts.rodeoQueen.description}</p>
      </div>
      
      <form onSubmit={handleSubmit}>
        <FormGroup title="Personal Information" description="Tell us about yourself.">
          <Input
            label="Full Name"
            id="fullName"
            name="fullName"
            placeholder="Enter your full name"
            value={formState.fullName}
            onChange={handleChange}
            required
            error={errors.fullName}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Email"
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email address"
              value={formState.email}
              onChange={handleChange}
              required
              error={errors.email}
            />
            
            <Input
              label="Phone Number"
              id="phone"
              name="phone"
              type="tel"
              placeholder="Enter your phone number"
              value={formState.phone}
              onChange={handleChange}
              required
              error={errors.phone}
            />
          </div>
          
          <Input
            label="Age"
            id="age"
            name="age"
            type="number"
            min="16"
            max="25"
            placeholder="Enter your age"
            value={formState.age}
            onChange={handleChange}
            required
            error={errors.age}
            helper="Applicants must be between 16 and 25 years old"
          />
        </FormGroup>
        
        <FormGroup title="Application Details" description="Share your experience and qualifications.">
          <TextArea
            label="Biography"
            id="biography"
            name="biography"
            placeholder="Tell us about yourself, your background, and why you want to be Rodeo Queen"
            value={formState.biography}
            onChange={handleChange}
            required
            error={errors.biography}
            helper="Minimum 100 characters recommended"
          />
          
          <TextArea
            label="Rodeo/Horsemanship Experience"
            id="experience"
            name="experience"
            placeholder="Describe your experience with rodeo events, horsemanship skills, and any relevant achievements"
            value={formState.experience}
            onChange={handleChange}
            required
            error={errors.experience}
          />
          
          <Input
            label="Photo URL (optional)"
            id="photoUrl"
            name="photoUrl"
            type="url"
            placeholder="Enter a URL to your recent photo (headshot recommended)"
            value={formState.photoUrl}
            onChange={handleChange}
            helper="A recent, high-quality photo of yourself. If provided, this may be used in promotional materials if selected."
          />
        </FormGroup>
        
        <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
          <h3 className="text-xl font-heading font-semibold text-primary-800 mb-4">
            Application Process
          </h3>
          
          <ol className="list-decimal list-inside space-y-3 text-gray-700">
            <li>
              <span className="font-medium">Application Submission</span>: Complete and submit this form.
            </li>
            <li>
              <span className="font-medium">Initial Review</span>: Our committee will review your application.
            </li>
            <li>
              <span className="font-medium">Interview</span>: Selected candidates will be invited for an interview.
            </li>
            <li>
              <span className="font-medium">Horsemanship Demonstration</span>: Finalists will demonstrate riding skills.
            </li>
            <li>
              <span className="font-medium">Selection</span>: The new Rodeo Queen will be announced at our upcoming event.
            </li>
          </ol>
          
          <p className="mt-4 text-sm text-gray-500">
            The selected Rodeo Queen will represent our organization at various events throughout the year
            and receive scholarship opportunities and prizes.
          </p>
        </div>
        
        <div className="flex justify-end gap-4 mt-8 mb-12">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/')}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            variant="accent"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default QueenRegistration;